import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "components";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useToaster } from "hooks";
import CustomDatepicker from "components/CustomDatePicker";
import moment from "moment";
import api from "services/api";
import * as Yup from "yup";

const initialValues = {
  promocode: "",
  coupon_percentage: "",
  expiry_date: "",
  status: "Pause",
};

const validationSchema = Yup.object({
  promocode: Yup.string()
    .required("Promocode is required")
    .min(3, "Promocode must be at least 3 characters")
    .max(20, "Promocode must be less than 20 characters"),
  coupon_percentage: Yup.number()
    .required("Percentage is required")
    .min(1, "Percentage must be at least 1")
    .max(100, "Percentage cannot exceed 100"),
  expiry_date: Yup.date()
    .transform((value, originalValue) =>
      moment(originalValue, "DD-MM-YYYY", true).isValid()
        ? moment(originalValue, "DD-MM-YYYY").toDate()
        : null
    )
    .required("Expiry date is required")
    .min(moment().add(1, 'day').toDate(), "Expiry date must be at least tomorrow"),
  status: Yup.string().required("Status is required"),
});

export const EditPromocode = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSuccessToaster, setErrorToaster } = useToaster();
  const [promocodeData, setPromocodeData] = useState({ ...initialValues });
  const [loading, setLoading] = useState(true);

  const fetchPromocodeDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/promocodes/${id}`);
      const promocode = response?.data?.data;
      
      // Format the data for the form
      const formattedData = {
        promocode: promocode.promocode,
        coupon_percentage: promocode.coupon_percentage,
        expiry_date: moment(promocode.expiry_date).format("DD-MM-YYYY"),
        status: promocode.status,
      };
      
      setPromocodeData(formattedData);
      console.log("Promocode details for edit:", promocode);
    } catch (error) {
      console.log("fetchPromocodeDetails error:", error);
      setErrorToaster("Failed to fetch promocode details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPromocodeDetails();
    }
  }, [id]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      expiry_date: moment(values.expiry_date, "DD-MM-YYYY").format("YYYY-MM-DD"),
    };

    try {
      const response = await api.put(`/promocodes/${id}`, payload);
      console.log("handleSubmit", response);
      if (response?.status === 200) {
        setSuccessToaster(response?.data?.message || "Promocode updated successfully");
        navigate("/promocodes");
      }
    } catch (error) {
      console.log("handleSubmit Error:", error);
      setErrorToaster(
        error?.response?.data?.message || "Failed to update promocode"
      );
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="content-wrapper">
          <div className="container-xxl flex-grow-1 container-p-y">
            <div className="d-flex justify-content-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/promocodes">Promocodes</Link>
              </li>
              <li className="breadcrumb-item active">Edit Promocode</li>
            </ol>
          </nav>
          <div className="row">
            <div className="col-12">
              <Formik
                initialValues={promocodeData}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  handleSubmit(values);
                  console.log("values", values);
                }}
                enableReinitialize
              >
                {({ values, errors, setFieldValue }) => {
                  return (
                    <Form>
                      <div className="card mb-4">
                        <h5 className="card-header">Edit Promocode</h5>
                        <hr className="my-0" />
                        <div className="card-body">
                          <div className="row mb-3">
                            <div className="col-md-6 col-12">
                              <label className="form-label">
                                Promocode <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="text"
                                className="form-control"
                                placeholder="Enter promocode"
                                name="promocode"
                                onChange={(e) => {
                                  const value = e.target.value.toUpperCase();
                                  if (/^[A-Z0-9]*$/.test(value)) {
                                    setFieldValue("promocode", value);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="promocode"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-6 col-12">
                              <label className="form-label">
                                Coupon Percentage <span className="invalid-form">*</span>
                              </label>
                              <Field
                                type="number"
                                className="form-control"
                                placeholder="Enter percentage"
                                name="coupon_percentage"
                                min="1"
                                max="100"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^[0-9]*$/.test(value) && parseInt(value) <= 100) {
                                    setFieldValue("coupon_percentage", value);
                                  }
                                }}
                              />
                              <ErrorMessage
                                name="coupon_percentage"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6 col-12">
                              <label className="form-label">
                                Expiry Date <span className="invalid-form">*</span>
                              </label>
                              <Field
                                className="form-control"
                                name="expiry_date"
                                value={values.expiry_date}
                                component={CustomDatepicker}
                                min={moment().add(1, 'day').format("DD-MM-YYYY")}
                              />
                              <ErrorMessage
                                name="expiry_date"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                            <div className="col-md-6 col-12">
                              <label className="form-label">
                                Status <span className="invalid-form">*</span>
                              </label>
                              <Field
                                as="select"
                                className="form-select"
                                name="status"
                              >
                                <option value="Pause">Pause</option>
                                <option value="Active">Active</option>
                                <option value="Stop">Stop</option>
                              </Field>
                              <ErrorMessage
                                name="status"
                                component="div"
                                className="invalid-form"
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-12">
                              <div className="alert alert-info">
                                <strong>Note:</strong> 
                                <ul className="mb-0 mt-2">
                                  <li>Promocode will be automatically converted to uppercase</li>
                                  <li>Only alphanumeric characters are allowed</li>
                                  <li>Expiry date must be at least tomorrow</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-primary"
                              type="submit"
                            >
                              Update Promocode
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => navigate("/promocodes")}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};
